import { useEffect, useState } from 'react'
import { ApiError, api } from '../lib/api'
import type {
  AdminPolicyDurationUnit,
  AdminPromotion,
  AdminPromotionCreatePayload,
  AdminPromotionRedemption,
  AdminPromotionUpdatePayload,
} from '../lib/adminTypes'
import './AdminPromotionPanel.css'

type Draft = {
  code: string
  name: string
  registrationStartsAt: string
  registrationEndsAt: string
  benefitDurationAmount: string
  benefitDurationUnit: AdminPolicyDurationUnit
  maxStoredUnlimited: boolean
  maxStoredFileSongs: string
  activeShareLimit: string
  retentionAmount: string
  retentionUnit: AdminPolicyDurationUnit
  shareRetentionAmount: string
  shareRetentionUnit: AdminPolicyDurationUnit
  shareDownloadLimit: string
  maxRedemptionsUnlimited: boolean
  maxRedemptions: string
  active: boolean
  adminNote: string
}

function localDate(value: string): string {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function dateParts(value: string, label: string): [number, number, number] {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) throw new Error(`${label}을(를) 입력해 주세요.`)
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const candidate = new Date(year, month - 1, day)
  if (
    candidate.getFullYear() !== year
    || candidate.getMonth() !== month - 1
    || candidate.getDate() !== day
  ) {
    throw new Error(`${label}이(가) 올바르지 않습니다.`)
  }
  return [year, month, day]
}

function startOfLocalDateIso(value: string, label: string): string {
  const [year, month, day] = dateParts(value, label)
  return new Date(year, month - 1, day, 0, 0, 0, 0).toISOString()
}

function endOfLocalDateIso(value: string, label: string): string {
  const [year, month, day] = dateParts(value, label)
  const exclusiveNextDay = new Date(year, month - 1, day + 1, 0, 0, 0, 0)
  exclusiveNextDay.setMilliseconds(exclusiveNextDay.getMilliseconds() - 1)
  return exclusiveNextDay.toISOString()
}

function positive(value: string, label: string): number {
  const n = Number(value)
  if (!Number.isInteger(n) || n <= 0) throw new Error(`${label}은(는) 1 이상의 정수여야 합니다.`)
  return n
}

function nonNegative(value: string, label: string): number {
  const n = Number(value)
  if (!Number.isInteger(n) || n < 0) throw new Error(`${label}은(는) 0 이상의 정수여야 합니다.`)
  return n
}

function newDraft(): Draft {
  const now = new Date()
  const end = new Date(now)
  end.setMonth(end.getMonth() + 6)
  return {
    code: '',
    name: '',
    registrationStartsAt: localDate(now.toISOString()),
    registrationEndsAt: localDate(end.toISOString()),
    benefitDurationAmount: '6',
    benefitDurationUnit: 'MONTHS',
    maxStoredUnlimited: false,
    maxStoredFileSongs: '30',
    activeShareLimit: '15',
    retentionAmount: '6',
    retentionUnit: 'MONTHS',
    shareRetentionAmount: '3',
    shareRetentionUnit: 'MONTHS',
    shareDownloadLimit: '100',
    maxRedemptionsUnlimited: false,
    maxRedemptions: '500',
    active: true,
    adminNote: '',
  }
}

function fromPromotion(value: AdminPromotion): Draft {
  return {
    code: value.code,
    name: value.name,
    registrationStartsAt: localDate(value.registrationStartsAt),
    registrationEndsAt: localDate(value.registrationEndsAt),
    benefitDurationAmount: String(value.benefitDurationAmount),
    benefitDurationUnit: value.benefitDurationUnit,
    maxStoredUnlimited: value.maxStoredFileSongs == null,
    maxStoredFileSongs: value.maxStoredFileSongs == null ? '' : String(value.maxStoredFileSongs),
    activeShareLimit: String(value.activeShareLimit),
    retentionAmount: String(value.retentionAmount),
    retentionUnit: value.retentionUnit,
    shareRetentionAmount: value.shareRetentionAmount == null ? '' : String(value.shareRetentionAmount),
    shareRetentionUnit: value.shareRetentionUnit ?? 'MONTHS',
    shareDownloadLimit: String(value.shareDownloadLimit),
    maxRedemptionsUnlimited: value.maxRedemptions == null,
    maxRedemptions: value.maxRedemptions == null ? '' : String(value.maxRedemptions),
    active: value.active,
    adminNote: value.adminNote ?? '',
  }
}

function payload(draft: Draft): AdminPromotionCreatePayload {
  const activeShareLimit = nonNegative(draft.activeShareLimit, '동시 공유 한도')
  return {
    code: draft.code.trim().toUpperCase(),
    name: draft.name.trim(),
    registrationStartsAt: startOfLocalDateIso(draft.registrationStartsAt, '코드 등록 시작일'),
    registrationEndsAt: endOfLocalDateIso(draft.registrationEndsAt, '코드 등록 종료일'),
    benefitDurationAmount: positive(draft.benefitDurationAmount, '사용자별 혜택기간'),
    benefitDurationUnit: draft.benefitDurationUnit,
    maxStoredFileSongs: draft.maxStoredUnlimited ? null : positive(draft.maxStoredFileSongs, '최대 음원 수'),
    activeShareLimit,
    retentionAmount: positive(draft.retentionAmount, '음원 보관기간'),
    retentionUnit: draft.retentionUnit,
    shareRetentionAmount: activeShareLimit > 0 ? positive(draft.shareRetentionAmount, '공유 최대기간') : null,
    shareRetentionUnit: activeShareLimit > 0 ? draft.shareRetentionUnit : null,
    shareDownloadLimit: positive(draft.shareDownloadLimit, '공유당 다운로드 한도'),
    maxRedemptions: draft.maxRedemptionsUnlimited ? null : positive(draft.maxRedemptions, '최대 적용 인원'),
    active: draft.active,
    adminNote: draft.adminNote.trim() || null,
  }
}

function messageFor(error: unknown): string {
  if (error instanceof Error && !(error instanceof ApiError)) return error.message
  if (error instanceof ApiError && error.status === 403) return '프로모션을 관리할 권한이 없습니다.'
  if (error instanceof ApiError && error.code === 'PROMOTION_CODE_DUPLICATE') return '이미 등록된 프로모션 코드입니다.'
  return '프로모션 정보를 처리하지 못했습니다.'
}

function fmt(value: string): string {
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString('ko-KR')
}

function fmtDate(value: string): string {
  const d = new Date(value)
  return Number.isNaN(d.getTime())
    ? value
    : new Intl.DateTimeFormat('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(d)
}

export function AdminPromotionPanel() {
  const [items, setItems] = useState<AdminPromotion[]>([])
  const [drafts, setDrafts] = useState<Record<number, Draft>>({})
  const [createDraft, setCreateDraft] = useState<Draft>(() => newDraft())
  const [loading, setLoading] = useState(true)
  const [savingId, setSavingId] = useState<number | 'create' | null>(null)
  const [message, setMessage] = useState('')
  const [redemptions, setRedemptions] = useState<Record<number, AdminPromotionRedemption[]>>({})
  const [openedRedemptions, setOpenedRedemptions] = useState<number | null>(null)

  useEffect(() => { void load() }, [])

  async function load() {
    setLoading(true)
    setMessage('')
    try {
      const next = await api.adminPromotions()
      setItems(next)
      setDrafts(Object.fromEntries(next.map((item) => [item.id, fromPromotion(item)])))
    } catch (error) {
      setMessage(messageFor(error))
    } finally {
      setLoading(false)
    }
  }

  function updateDraft(id: number, patch: Partial<Draft>) {
    setDrafts((current) => ({ ...current, [id]: { ...current[id], ...patch } }))
  }

  async function createPromotion() {
    let body: AdminPromotionCreatePayload
    try {
      body = payload(createDraft)
      if (!body.code) throw new Error('프로모션 코드를 입력해 주세요.')
      if (!body.name) throw new Error('프로모션 이름을 입력해 주세요.')
    } catch (error) {
      setMessage(messageFor(error))
      return
    }

    setSavingId('create')
    setMessage('')
    try {
      const created = await api.adminCreatePromotion(body)
      setItems((current) => [created, ...current])
      setDrafts((current) => ({ ...current, [created.id]: fromPromotion(created) }))
      setCreateDraft(newDraft())
      setMessage(`${created.code} 프로모션을 만들었습니다.`)
    } catch (error) {
      setMessage(messageFor(error))
    } finally {
      setSavingId(null)
    }
  }

  async function save(item: AdminPromotion) {
    const draft = drafts[item.id]
    if (!draft) return
    let body: AdminPromotionUpdatePayload
    try {
      const full = payload(draft)
      const { code, ...rest } = full
      void code
      body = rest
    } catch (error) {
      setMessage(messageFor(error))
      return
    }

    setSavingId(item.id)
    setMessage('')
    try {
      const updated = await api.adminUpdatePromotion(item.id, body)
      setItems((current) => current.map((value) => value.id === item.id ? updated : value))
      setDrafts((current) => ({ ...current, [item.id]: fromPromotion(updated) }))
      setMessage(`${updated.code} 프로모션 정책을 저장했습니다.`)
    } catch (error) {
      setMessage(messageFor(error))
    } finally {
      setSavingId(null)
    }
  }

  async function toggleActive(item: AdminPromotion) {
    setSavingId(item.id)
    setMessage('')
    try {
      const updated = await api.adminSetPromotionActive(item.id, !item.active)
      setItems((current) => current.map((value) => value.id === item.id ? updated : value))
      setDrafts((current) => ({ ...current, [item.id]: fromPromotion(updated) }))
      setMessage(updated.active ? `${updated.code} 신규 적용을 다시 열었습니다.` : `${updated.code} 신규 적용을 중지했습니다. 기존 적용자의 혜택기간은 유지됩니다.`)
    } catch (error) {
      setMessage(messageFor(error))
    } finally {
      setSavingId(null)
    }
  }

  async function toggleRedemptions(item: AdminPromotion) {
    if (openedRedemptions === item.id) {
      setOpenedRedemptions(null)
      return
    }
    try {
      const next = await api.adminPromotionRedemptions(item.id)
      setRedemptions((current) => ({ ...current, [item.id]: next }))
      setOpenedRedemptions(item.id)
    } catch (error) {
      setMessage(messageFor(error))
    }
  }

  if (loading) return <section className="admin-card admin-message"><p>프로모션을 불러오고 있습니다.</p></section>

  const renderFields = (draft: Draft, change: (patch: Partial<Draft>) => void, codeEditable: boolean) => (
    <div className="admin-promotion-fields">
      <label className="admin-promotion-field">
        <span>프로모션 코드</span>
        <input disabled={!codeEditable} maxLength={40} value={draft.code} onChange={(e) => change({ code: e.target.value.toUpperCase() })} />
      </label>
      <label className="admin-promotion-field">
        <span>프로모션 이름</span>
        <input maxLength={120} value={draft.name} onChange={(e) => change({ name: e.target.value })} />
      </label>
      <label className="admin-promotion-field">
        <span>코드 등록 시작</span>
        <input type="date" value={draft.registrationStartsAt} onChange={(e) => change({ registrationStartsAt: e.target.value })} />
      </label>
      <label className="admin-promotion-field">
        <span>코드 등록 종료</span>
        <input type="date" value={draft.registrationEndsAt} onChange={(e) => change({ registrationEndsAt: e.target.value })} />
      </label>

      <div className="admin-promotion-field">
        <span className="admin-promotion-field-label">사용자별 혜택기간</span>
        <div className="admin-promotion-duration">
          <input aria-label="사용자별 혜택기간" inputMode="numeric" value={draft.benefitDurationAmount} onChange={(e) => change({ benefitDurationAmount: e.target.value })} />
          <select aria-label="혜택기간 단위" value={draft.benefitDurationUnit} onChange={(e) => change({ benefitDurationUnit: e.target.value as AdminPolicyDurationUnit })}>
            <option value="DAYS">일</option>
            <option value="MONTHS">개월</option>
          </select>
        </div>
      </div>

      <div className="admin-promotion-field">
        <span className="admin-promotion-field-label">최대 음원 수</span>
        <div className="admin-promotion-limit">
          <input aria-label="최대 음원 수" inputMode="numeric" disabled={draft.maxStoredUnlimited} value={draft.maxStoredFileSongs} placeholder={draft.maxStoredUnlimited ? '무제한' : ''} onChange={(e) => change({ maxStoredFileSongs: e.target.value })} />
          <label className="admin-promotion-inline-check"><input type="checkbox" checked={draft.maxStoredUnlimited} onChange={(e) => change({ maxStoredUnlimited: e.target.checked })} /> 무제한</label>
        </div>
      </div>

      <label className="admin-promotion-field">
        <span>동시 Active 공유</span>
        <input inputMode="numeric" value={draft.activeShareLimit} onChange={(e) => change({ activeShareLimit: e.target.value })} />
      </label>

      <div className="admin-promotion-field">
        <span className="admin-promotion-field-label">음원·작업 보관기간</span>
        <div className="admin-promotion-duration">
          <input aria-label="음원·작업 보관기간" inputMode="numeric" value={draft.retentionAmount} onChange={(e) => change({ retentionAmount: e.target.value })} />
          <select aria-label="보관기간 단위" value={draft.retentionUnit} onChange={(e) => change({ retentionUnit: e.target.value as AdminPolicyDurationUnit })}>
            <option value="DAYS">일</option>
            <option value="MONTHS">개월</option>
          </select>
        </div>
      </div>

      <div className="admin-promotion-field">
        <span className="admin-promotion-field-label">공유 최대기간</span>
        <div className="admin-promotion-duration">
          <input aria-label="공유 최대기간" inputMode="numeric" disabled={Number(draft.activeShareLimit) <= 0} value={draft.shareRetentionAmount} onChange={(e) => change({ shareRetentionAmount: e.target.value })} />
          <select aria-label="공유기간 단위" disabled={Number(draft.activeShareLimit) <= 0} value={draft.shareRetentionUnit} onChange={(e) => change({ shareRetentionUnit: e.target.value as AdminPolicyDurationUnit })}>
            <option value="DAYS">일</option>
            <option value="MONTHS">개월</option>
          </select>
        </div>
      </div>

      <label className="admin-promotion-field">
        <span>공유당 다운로드</span>
        <input inputMode="numeric" value={draft.shareDownloadLimit} onChange={(e) => change({ shareDownloadLimit: e.target.value })} />
      </label>

      <div className="admin-promotion-field">
        <span className="admin-promotion-field-label">최대 적용 인원</span>
        <div className="admin-promotion-limit">
          <input aria-label="최대 적용 인원" inputMode="numeric" disabled={draft.maxRedemptionsUnlimited} value={draft.maxRedemptions} placeholder={draft.maxRedemptionsUnlimited ? '무제한' : ''} onChange={(e) => change({ maxRedemptions: e.target.value })} />
          <label className="admin-promotion-inline-check"><input type="checkbox" checked={draft.maxRedemptionsUnlimited} onChange={(e) => change({ maxRedemptionsUnlimited: e.target.checked })} /> 무제한</label>
        </div>
      </div>

      <label className="admin-promotion-toggle"><input type="checkbox" checked={draft.active} onChange={(e) => change({ active: e.target.checked })} /><span>신규 코드 적용 허용</span></label>

      <label className="admin-promotion-note">
        <span>관리 메모</span>
        <textarea maxLength={1000} value={draft.adminNote} onChange={(e) => change({ adminNote: e.target.value })} />
      </label>
    </div>
  )

  return (
    <section className="admin-promotion-section" aria-label="프로모션 관리">
      <div className="admin-policy-intro">
        <div>
          <h2>프로모션</h2>
          <p>기본 요금제를 바꾸지 않고 코드별 혜택을 일정 기간 덧씌웁니다. 기간이 끝나면 사용자는 자동으로 원래 요금제로 돌아갑니다.</p>
        </div>
        <div className="admin-policy-safety">코드 중지는 신규 적용만 막으며 기존 적용자의 만료일과 이미 생성된 공유를 소급 삭제하지 않습니다.</div>
      </div>

      {message && <div className="admin-policy-message" role="status">{message}</div>}

      <article className="admin-policy-card admin-promotion-create">
        <header><div><h3>새 프로모션</h3><small>코드는 생성 후 변경하지 않습니다.</small></div><span className="admin-badge">NEW</span></header>
        {renderFields(createDraft, (patch) => setCreateDraft((current) => ({ ...current, ...patch })), true)}
        <footer className="admin-policy-actions">
          <button className="admin-button primary" type="button" disabled={savingId === 'create'} onClick={() => void createPromotion()}>{savingId === 'create' ? '생성 중…' : '프로모션 생성'}</button>
        </footer>
      </article>

      <div className="admin-policy-grid">
        {items.map((item) => {
          const draft = drafts[item.id]
          if (!draft) return null
          return (
            <article className="admin-policy-card" key={item.id}>
              <header>
                <div><h3>{item.name}</h3><small>{item.code} · 등록 {fmtDate(item.registrationStartsAt)} ~ {fmtDate(item.registrationEndsAt)}</small></div>
                <span className={`admin-badge ${item.active ? '' : 'muted'}`}>{item.active ? 'ACTIVE' : 'STOPPED'}</span>
              </header>
              <div className="admin-promotion-usage">
                <strong>{item.redemptionCount}{item.maxRedemptions == null ? '명 적용' : ` / ${item.maxRedemptions}명 적용`}</strong>
                <span>사용자별 혜택 {item.benefitDurationAmount}{item.benefitDurationUnit === 'MONTHS' ? '개월' : '일'}</span>
              </div>
              {renderFields(draft, (patch) => updateDraft(item.id, patch), false)}
              <footer className="admin-policy-actions">
                <button className="admin-button primary" type="button" disabled={savingId === item.id} onClick={() => void save(item)}>정책 저장</button>
                <button className="admin-button" type="button" disabled={savingId === item.id} onClick={() => void toggleActive(item)}>{item.active ? '신규 적용 중지' : '다시 활성화'}</button>
                <button className="admin-button" type="button" onClick={() => void toggleRedemptions(item)}>{openedRedemptions === item.id ? '적용자 닫기' : '적용자 보기'}</button>
              </footer>
              {openedRedemptions === item.id && (
                <div className="admin-promotion-redemptions">
                  {(redemptions[item.id] ?? []).length === 0 ? <p>아직 적용자가 없습니다.</p> : (
                    <table className="admin-table"><thead><tr><th>사용자</th><th>적용일</th><th>혜택 만료</th></tr></thead><tbody>
                      {(redemptions[item.id] ?? []).map((row) => <tr key={row.id}><td>{row.centralUserId}</td><td>{fmt(row.redeemedAt)}</td><td>{fmt(row.benefitExpiresAt)}</td></tr>)}
                    </tbody></table>
                  )}
                </div>
              )}
            </article>
          )
        })}
      </div>
    </section>
  )
}
